import { z } from 'zod';

export const RunStatus = z.enum(['PENDING', 'PARSING', 'RENDERING', 'COMPLETE', 'FAILED']);
export type RunStatus = z.infer<typeof RunStatus>;

export const RunListItem = z.object({
  id: z.string(),
  // `month` is the YYYY-MM derived from the CSV's first data row. It is only
  // known after the parser has consumed that row, which happens asynchronously
  // in the queue worker (T-034). Runs in PENDING/PARSING status therefore do
  // not yet have a month and surface as `null`. Once parsed, the value must
  // match the strict YYYY-MM shape — the API must never emit a garbage
  // placeholder string here.
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .nullable(),
  status: RunStatus,
  uploadedAt: z.string().datetime(),
  pdfCount: z.number().int().nonnegative().nullable(),
  customerCount: z.number().int().nonnegative().nullable(),
  rowCount: z.number().int().nonnegative().nullable(),
});
export type RunListItem = z.infer<typeof RunListItem>;

export const RunListResponse = z.object({
  items: z.array(RunListItem),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type RunListResponse = z.infer<typeof RunListResponse>;

export const RunCustomerLine = z.object({
  customerId: z.string(),
  name: z.string(),
  totalCalls: z.number().int().nonnegative(),
  totalSeconds: z.number().int().nonnegative(),
  totalCostPence: z.number().int().nonnegative(),
  pdfReady: z.boolean(),
});
export type RunCustomerLine = z.infer<typeof RunCustomerLine>;

export const RunDetail = RunListItem.extend({
  originalFilename: z.string(),
  errorMessage: z.string().nullable().optional(),
  customers: z.array(RunCustomerLine),
});
export type RunDetail = z.infer<typeof RunDetail>;

export const CreateRunResponse = z.object({
  runId: z.string(),
  status: RunStatus,
});
export type CreateRunResponse = z.infer<typeof CreateRunResponse>;
